import { t } from "../i18n";
// Money display. Formatting only: nothing here is stored, converted between currencies, or applied to
// calculations. Two kinds of amounts exist in this app:
//  - payroll (wages, car, tips, advances, salaries) is STORED IN THOUSANDS of dinars (the forms say "بالألف");
//    formatPayroll shows the real dinar amount: stored 25 -> "25,000 د.ع".
//  - purchases are stored in full units of their own currency (IQD whole dinars, USD with cents).
// Missing values show "—". Digits are isolated left-to-right so signs and symbols stay put in RTL text.
import { isEnglish } from "../i18n";
const LRI = "⁦";
const PDI = "⁩";
const NBSP = " ";

export const PAYROLL_UNIT = 1000; // stored payroll amounts are thousands of dinars
export const IQD_SUFFIX = isEnglish ? "IQD" : "د.ع"; // only the label follows the language, never the amount
export const MISSING = "—";

export const CURRENCIES = [
  { code: "IQD", label: t("دينار عراقي (IQD)") },
  { code: "USD", label: t("دولار أمريكي (USD)") },
];
export const currencyIndicator = (code) => (code === "USD" ? "$" : IQD_SUFFIX);

const wholeNumber = new Intl.NumberFormat("en-US", {
  useGrouping: true,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const twoDecimals = new Intl.NumberFormat("en-US", {
  useGrouping: true,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

// Stored payroll value (thousands) -> whole dinars. Rounded to whole dinars, never changes the stored value.
export function toDinars(stored) {
  const n = toNumber(stored);
  return n === null ? null : Math.round(n * PAYROLL_UNIT);
}

// Whole dinars -> "1,500,000 د.ع" (round half away from zero; never "-0").
export function formatIQD(value) {
  const n = toNumber(value);
  if (n === null) return MISSING;
  const rounded = Math.sign(n) * Math.round(Math.abs(n));
  return `${LRI}${rounded < 0 ? "-" : ""}${wholeNumber.format(Math.abs(rounded))}${PDI}${NBSP}${IQD_SUFFIX}`;
}

// Stored payroll value (thousands of dinars) -> "25,000 د.ع".
export const formatPayroll = (stored) => formatIQD(toDinars(stored));

// US dollars -> "$100.00" / "-$100.00".
export function formatUSD(value) {
  const n = toNumber(value);
  if (n === null) return MISSING;
  const rounded = Math.sign(n) * (Math.round(Math.abs(n) * 100) / 100);
  return `${LRI}${rounded < 0 ? "-" : ""}$${twoDecimals.format(Math.abs(rounded))}${PDI}`;
}

// A purchase amount in its own currency. Unknown/missing currency is shown as IQD (the legacy convention).
export const formatByCurrency = (amount, currency) =>
  currency === "USD" ? formatUSD(amount) : formatIQD(amount);

// Plain text of a formatted amount (no direction marks), for input values that users may copy.
export const stripBidi = (text) => String(text).replace(/[\u2066\u2069]/g, "");

// Totals per currency: IQD and USD are summed separately and never combined (in minor units).
export function totalsByCurrency(
  items,
  amountOf = (i) => i.total_price,
  currencyOf = (i) => i.currency
) {
  const cents = { IQD: 0, USD: 0 };
  for (const item of items) {
    const code = currencyOf(item) === "USD" ? "USD" : "IQD";
    cents[code] += Math.round((toNumber(amountOf(item)) || 0) * 100);
  }
  return { IQD: cents.IQD / 100, USD: cents.USD / 100 };
}
